import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_FPS, Z_INDEX, COMPOSITOR_VERSION, DEFAULT_SHADOW } from '../constants';
import { CompositorManifest, CompositorRecordingData, StreamSegmentInput, BackgroundConfig, AudioSegmentInput } from '../types/manifest';
import { CompositorLayer, LayerSegment, SegmentShape, SegmentShadow, AudioLayer, AudioLayerSegment } from '../types/layers';
import { SourceResolver } from './sourceResolver';
import { msToFrames, calculateDurationFrames } from './frameCalculator';

export interface ManifestBuilderOptions {
  fps?: number;
  sourceResolver: SourceResolver;
  pendingBlobs: Map<string, Blob>;
  pendingBlobUrls?: Map<string, string>;
  segmentBlobs?: Map<string, Map<number, Blob>>;
}

function resolveSegmentEndTime(
  segment: StreamSegmentInput,
  totalDurationMs: number
): number {
  if (segment.tsEnd === null || segment.tsEnd === undefined) {
    return totalDurationMs;
  }
  return segment.tsEnd;
}

function resolveSegmentGeometry(
  segment: StreamSegmentInput,
  streamType: 'screen' | 'webcam',
  layout: CompositorRecordingData['layout']
): { x: number; y: number; width: number; height: number } {
  const defaultLayout = streamType === 'webcam'
    ? layout.webcam
    : layout.screen;

  return {
    x: segment.x ?? defaultLayout?.x ?? 0,
    y: segment.y ?? defaultLayout?.y ?? 0,
    width: segment.width ?? defaultLayout?.width ?? layout.canvas.width,
    height: segment.height ?? defaultLayout?.height ?? layout.canvas.height,
  };
}

function buildSegmentShape(
  segment: StreamSegmentInput,
  width: number,
  height: number,
  streamType: 'screen' | 'webcam'
): SegmentShape {
  // layout.*.segments is now the single source of truth (set in buildCompositorManifest)
  const isCircle = segment.isCircle ?? false;
  const hasExplicitCornerRadius = segment.cornerRadius !== undefined && segment.cornerRadius !== null;
  let cornerRadius = segment.cornerRadius ?? 0;

  let borderRadius: string;
  let clipPath: string | undefined;

  if (isCircle) {
    borderRadius = '50%';
    clipPath = 'circle(50% at center)';
  } else if (cornerRadius > 0) {
    borderRadius = `${cornerRadius}px`;
  } else if (hasExplicitCornerRadius && cornerRadius === 0) {
    borderRadius = '0px';
  } else {
    const minSide = Math.min(width, height);
    const defaultRadius = Math.max(8, minSide * 0.06);
    cornerRadius = Math.min(defaultRadius, minSide / 32);
    borderRadius = `${cornerRadius}px`;
  }

  // Apply shadow based on stream type
  const shadowConfig = streamType === 'webcam' ? DEFAULT_SHADOW.WEBCAM : DEFAULT_SHADOW.SCREEN;
  const shadow: SegmentShadow = {
    color: shadowConfig.color,
    blur: shadowConfig.blur,
    offsetX: shadowConfig.offsetX,
    offsetY: shadowConfig.offsetY,
  };

  return {
    isCircle,
    cornerRadius,
    borderRadius,
    clipPath,
    shadow,
  };
}

function resolveImageUrl(url: string | undefined, imageKey: string | undefined): string | undefined {
  // If we have a valid imageKey (S3 path), prefer resolving through the proxy
  const key = imageKey || undefined;
  
  // Check if URL is a blob URL or invalid for remote loading
  const isBlobUrl = url?.startsWith('blob:');
  const isDataUrl = url?.startsWith('data:');
  
  if (isBlobUrl || isDataUrl) {
    // Blob/data URLs won't work in compositor - use imageKey via proxy if available
    if (key && !key.startsWith('blob:') && !key.startsWith('http')) {
      return `/api/s3-image-proxy?key=${encodeURIComponent(key)}`;
    }
    return undefined;
  }
  
  // If URL is a raw S3 key (no protocol), resolve through proxy
  if (url && !url.startsWith('http') && !url.startsWith('/') && !url.startsWith('blob:')) {
    return `/api/s3-image-proxy?key=${encodeURIComponent(url)}`;
  }
  
  // Valid URL (presigned or absolute), use as-is
  return url;
}

function buildBackgroundConfig(bg: CompositorRecordingData['layout']['background']): BackgroundConfig {
  console.log('[ManifestBuilder] 🎨 buildBackgroundConfig input:', bg);
  
  let result: BackgroundConfig;
  switch (bg.type) {
    case 'color':
      result = { type: 'color', color: bg.value || '#000000' };
      console.log('[ManifestBuilder] Built COLOR config:', result);
      return result;
    case 'image':
      const resolvedUrl = resolveImageUrl(bg.imageUrl || bg.imageKey, bg.imageKey);
      result = {
        type: 'image',
        imageUrl: resolvedUrl,
        imageFit: 'cover',
      };
      console.log('[ManifestBuilder] Built IMAGE config:', result);
      return result;
    case 'gradient':
      result = { type: 'gradient', gradient: bg.value };
      console.log('[ManifestBuilder] Built GRADIENT config:', result);
      return result;
    default:
      result = { type: 'color', color: '#000000' };
      console.log('[ManifestBuilder] Unknown bg type, defaulting to black. Input was:', bg);
      return result;
  }
}

function buildLayerSegment(
  segment: StreamSegmentInput,
  streamType: 'screen' | 'webcam',
  totalDurationMs: number,
  fps: number,
  layout: CompositorRecordingData['layout'],
  options: ManifestBuilderOptions
): LayerSegment {
  const tsEndMs = resolveSegmentEndTime(segment, totalDurationMs);
  const startFrame = msToFrames(segment.tsStart, fps);
  const endFrame = msToFrames(tsEndMs, fps);
  let geometry = resolveSegmentGeometry(segment, streamType, layout);

  // When isCircle is true, constrain to a square using the smaller dimension
  // This matches the canvas behavior where circular webcam uses a square container
  // layout.*.segments is now the single source of truth (set in buildCompositorManifest)
  const isCircle = segment.isCircle ?? false;
  if (isCircle && geometry.width !== geometry.height) {
    const diameter = Math.min(geometry.width, geometry.height);
    // Center the square within the original bounds
    const offsetX = (geometry.width - diameter) / 2;
    const offsetY = (geometry.height - diameter) / 2;
    geometry = {
      x: geometry.x + offsetX,
      y: geometry.y + offsetY,
      width: diameter,
      height: diameter,
    };
  }

  return {
    segmentIndex: segment.segmentIndex,
    segmentId: `${streamType}-${segment.segmentIndex}`,
    startFrame,
    endFrame,
    durationFrames: Math.max(endFrame - startFrame, 1),
    tsStartMs: segment.tsStart,
    tsEndMs,
    // Original video file timestamps - used for seeking when windowing
    originalVideoStartMs: segment.tsStart,
    originalVideoEndMs: tsEndMs,
    // No offset initially - video plays from the start of the file
    videoStartFrame: 0,
    x: geometry.x,
    y: geometry.y,
    width: geometry.width,
    height: geometry.height,
    shape: buildSegmentShape(segment, geometry.width, geometry.height, streamType),
    source: options.sourceResolver.resolve(
      segment,
      streamType,
      options.pendingBlobs,
      options.segmentBlobs,
      options.pendingBlobUrls
    ),
    // Always enable audio for video segments - let the video file itself determine if audio exists
    hasAudio: true,
    volume: 1,
    isMirrored: segment.isMirrored ?? false,
    // Avatar properties (static image instead of video)
    isAvatar: segment.isAvatar,
    avatarUrl: segment.avatarUrl,
  };
}

function buildLayer(
  id: string,
  type: 'screen' | 'webcam',
  zIndex: number,
  segments: StreamSegmentInput[],
  totalDurationMs: number,
  fps: number,
  layout: CompositorRecordingData['layout'],
  options: ManifestBuilderOptions
): CompositorLayer {
  return {
    id,
    type,
    zIndex,
    segments: segments.map(seg =>
      buildLayerSegment(seg, type, totalDurationMs, fps, layout, options)
    ),
    visible: true,
  };
}

function resolveAudioSegmentEndTime(
  segment: AudioSegmentInput,
  totalDurationMs: number
): number {
  if (segment.tsEnd === null || segment.tsEnd === undefined) {
    return totalDurationMs;
  }
  return segment.tsEnd;
}

function buildAudioLayerSegment(
  segment: AudioSegmentInput,
  totalDurationMs: number,
  fps: number,
  options: ManifestBuilderOptions
): AudioLayerSegment {
  const tsEndMs = resolveAudioSegmentEndTime(segment, totalDurationMs);
  const startFrame = msToFrames(segment.tsStart, fps);
  const endFrame = msToFrames(tsEndMs, fps);

  return {
    segmentIndex: segment.segmentIndex,
    segmentId: `audio-${segment.segmentIndex}`,
    startFrame,
    endFrame,
    durationFrames: Math.max(endFrame - startFrame, 1),
    tsStartMs: segment.tsStart,
    tsEndMs,
    // Original audio file timestamps - used for seeking when windowing
    originalAudioStartMs: segment.tsStart,
    originalAudioEndMs: tsEndMs,
    // No offset initially - audio plays from the start of the file
    audioStartFrame: 0,
    source: options.sourceResolver.resolveAudio(
      segment,
      options.pendingBlobs,
      options.segmentBlobs,
      options.pendingBlobUrls
    ),
    volume: segment.isMuted ? 0 : 1,
    hasMic: segment.hasMic,
    hasSystemAudio: segment.hasSystemAudio,
    isMuted: segment.isMuted,
  };
}

function buildAudioLayer(
  segments: AudioSegmentInput[],
  totalDurationMs: number,
  fps: number,
  options: ManifestBuilderOptions
): AudioLayer {
  return {
    id: 'audio',
    type: 'audio',
    segments: segments.map(seg =>
      buildAudioLayerSegment(seg, totalDurationMs, fps, options)
    ),
    volume: 1,
  };
}

function resolveAudioConflicts(layers: CompositorLayer[]): void {
  interface AudioSegment {
    layer: CompositorLayer;
    segment: LayerSegment;
    segmentIndex: number;
    priority: number;
  }

  const audioSegments: AudioSegment[] = [];

  layers.forEach(layer => {
    layer.segments.forEach((segment, index) => {
      if (segment.hasAudio && segment.volume > 0) {
        audioSegments.push({
          layer,
          segment,
          segmentIndex: index,
          priority: layer.type === 'webcam' ? 2 : 1,
        });
      }
    });
  });

  audioSegments.sort((a, b) => {
    if (b.priority !== a.priority) return b.priority - a.priority;
    if (a.segment.startFrame !== b.segment.startFrame) return a.segment.startFrame - b.segment.startFrame;
    return a.segmentIndex - b.segmentIndex;
  });

  for (let i = 0; i < audioSegments.length; i++) {
    const current = audioSegments[i];
    if (current.segment.volume === 0) continue;

    for (let j = i + 1; j < audioSegments.length; j++) {
      const other = audioSegments[j];
      if (other.segment.volume === 0) continue;

      const overlaps =
        current.segment.startFrame < other.segment.endFrame &&
        current.segment.endFrame > other.segment.startFrame;

      if (overlaps) {
        other.segment.volume = 0;
      }
    }
  }
}

export function buildCompositorManifest(
  recordingData: CompositorRecordingData,
  options: ManifestBuilderOptions
): CompositorManifest {
  const fps = options.fps ?? DEFAULT_FPS;
  const { duration, layout, streams } = recordingData;

  const durationFrames = calculateDurationFrames(duration, fps);
  const layers: CompositorLayer[] = [];

  const mergeKeysFromStreams = (
    layoutSegs: StreamSegmentInput[] | undefined,
    streamSegs: StreamSegmentInput[] | undefined
  ): StreamSegmentInput[] | undefined => {
    if (!layoutSegs?.length) return streamSegs;
    if (!streamSegs?.length) return layoutSegs;
    return layoutSegs.map((seg) => {
      if (seg.key) return seg;
      const streamSeg = streamSegs.find(s => s.segmentIndex === seg.segmentIndex);
      if (streamSeg?.key && streamSeg.uploadStatus === 'completed') {
        return { ...seg, key: streamSeg.key, uploadStatus: 'completed' };
      }
      return seg;
    });
  };

  const screenSegments = mergeKeysFromStreams(
    (layout.screen as any)?.segments,
    streams.screen?.segments
  );
  const webcamSegments = mergeKeysFromStreams(
    (layout.webcam as any)?.segments,
    streams.webcam?.segments
  );

  if (screenSegments?.length) {
    layers.push(
      buildLayer(
        'screen',
        'screen',
        Z_INDEX.SCREEN,
        screenSegments,
        duration,
        fps,
        layout,
        options
      )
    );
  }

  if (webcamSegments?.length) {
    layers.push(
      buildLayer(
        'webcam',
        'webcam',
        Z_INDEX.WEBCAM,
        webcamSegments,
        duration,
        fps,
        layout,
        options
      )
    );
  }

  // Build audio layer if dedicated audio stream exists
  let audioLayer: AudioLayer | undefined;
  if (streams.audio?.segments?.length) {
    audioLayer = buildAudioLayer(
      streams.audio.segments,
      duration,
      fps,
      options
    );
    // When dedicated audio stream exists, mute all video layer audio
    // (audio is entirely in the dedicated audio stream)
    layers.forEach(layer => {
      layer.segments.forEach(segment => {
        segment.hasAudio = false;
        segment.volume = 0;
      });
    });
  } else {
    // No dedicated audio stream - use video audio with conflict resolution
    resolveAudioConflicts(layers);
  }

  return {
    id: uuidv4(),
    canvas: {
      width: layout.canvas.width,
      height: layout.canvas.height,
      aspectRatio: layout.canvas.aspectRatio || '16:9',
    },
    durationMs: duration,
    durationFrames,
    fps,
    background: buildBackgroundConfig(layout.background),
    layers,
    audioLayer,
    createdAt: Date.now(),
    version: COMPOSITOR_VERSION,
  };
}

export function isSeparateStreamRecording(metadata: any): boolean {
  return (
    metadata?.version === '2.1' &&
    (metadata?.streams?.webcam?.segments?.length > 0 ||
      metadata?.streams?.screen?.segments?.length > 0)
  );
}
